import { Dialog } from "@mui/material"
import React from "react"

import {
  BG_VERY_LIGHT,
  BORDER_MUTED,
  PRIMARY_BLUE,
  TEXT_BODY,
  TEXT_HEADING,
  TEXT_PRIMARY,
  TEXT_SOFT,
} from "@/constants/colors"
import i18n from "@/i18n"

const STYLES = {
  dialogContent: "flex flex-col bg-white",
  dialogBody: "flex flex-col items-center gap-5 px-5 pb-10 pt-5 text-center sm:px-10",
  closeButton: "ml-auto flex h-8 w-8 items-center justify-center rounded-full border-0 text-[22px] leading-none",
  iconWrapper: "flex h-[88px] w-[100px] items-center justify-center",
  iconImage: "block h-full w-full",
  textWrapper: "space-y-4",
  heading: "m-0 text-[22px] font-medium tracking-[-0.5px]",
  description: "m-0 max-w-[360px] whitespace-pre-line text-[14px] leading-5",
  buttonStack: "flex w-full flex-col gap-3",
  primaryButton:
    "flex h-10 w-full items-center justify-center rounded-[10px] border-0 text-[14px] font-medium text-white no-underline",
  secondaryButton:
    "flex h-10 w-full items-center justify-center rounded-[10px] border border-solid bg-white text-[14px] font-medium no-underline",
} as const

type Action = {
  label: string
  onClick?: () => void
  href?: string
  external?: boolean
  disabled?: boolean
}

type ActionButtonProps = {
  action: Action
  className: string
  style?: React.CSSProperties
  testId: string
}

type Props = {
  open: boolean
  onClose: () => void
  icon: string
  title: string
  description: string
  primaryAction: Action
  secondaryAction?: Action
  testId?: string
}

const ActionButton: React.FC<ActionButtonProps> = ({ action, className, style, testId }) => {
  if (action.href) {
    return (
      <a
        href={action.href}
        className={className}
        style={style}
        onClick={action.onClick}
        target={action.external ? "_blank" : undefined}
        rel={action.external ? "noopener noreferrer" : undefined}
        aria-disabled={action.disabled}
        data-testid={testId}
      >
        {action.label}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={action.onClick}
      className={className}
      style={style}
      disabled={action.disabled}
      data-testid={testId}
    >
      {action.label}
    </button>
  )
}

const ReservationStatusModal: React.FC<Props> = ({
  open,
  onClose,
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  testId = "public-reservation-status-modal",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: { xs: 0, sm: "10px" }, overflow: "hidden" },
      }}
    >
      <div className={STYLES.dialogContent} data-testid={testId}>
        <div className={STYLES.dialogBody}>
          <button
            type="button"
            onClick={onClose}
            className={STYLES.closeButton}
            style={{ backgroundColor: BG_VERY_LIGHT, color: TEXT_SOFT }}
            aria-label={i18n.t("app.public.venue.modal.close")}
          >
            ×
          </button>

          <div className={STYLES.iconWrapper} aria-hidden data-testid={`${testId}-icon`}>
            <img src={icon} alt="" className={STYLES.iconImage} />
          </div>

          <div className={STYLES.textWrapper}>
            <h2 className={STYLES.heading} style={{ color: TEXT_HEADING }}>
              {title}
            </h2>
            <p className={STYLES.description} style={{ color: TEXT_BODY }}>
              {description}
            </p>
          </div>

          <div className={STYLES.buttonStack}>
            <ActionButton
              action={primaryAction}
              className={STYLES.primaryButton}
              style={{ backgroundColor: PRIMARY_BLUE }}
              testId={`${testId}-primary-btn`}
            />

            {secondaryAction ? (
              <ActionButton
                action={secondaryAction}
                className={STYLES.secondaryButton}
                style={{ borderColor: BORDER_MUTED, color: TEXT_PRIMARY }}
                testId={`${testId}-secondary-btn`}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ReservationStatusModal
